import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import "./msgPageTextDisplay.css";
import {
  SEND_SMS_TEXT_STRIPPED_RETURN,
  JOIN_SMS_THREAD,
  HEARTBEAT_SMS_THREAD,
  LEAVE_SMS_THREAD,
} from "../../../../utils/GraphQL/mutations";
import TAB_DEVICE_ID from "../../../../utils/presence/tabDeviceId";
import Auth from "../../../../utils/auth";
import {
  GET_PHONE_TEXTS_BY_ID,
  GET_PHONE_TEXTS_SINCE,
  QUERY_CLIENT_PHOTOS,
} from "../../../../utils/GraphQL/queries";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import smsErrorMessage from "../../../../utils/smsErrorMessage";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { CircularProgress } from "@mui/material";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copyImageToClip from "../../../../utils/copyImageToClip";
import { baImageUrl } from "../../../../utils/baImageUrl";
import MsgImgShuffle from "../MsgImgShuffle/MsgImgShuffle";
import OfficeShortcuts from "../OfficeShortcuts/OfficeShortcuts";
// const clearText = {
//   _id: "",
//   phoneNumber: "",
//   numberType: "",
//   title: "",
//   recentTime: "",
//   textMessages: [],
//   unreadMessages: false,
//   hasTexts: true,
// };
// T3.1 thread pagination (2026-09-08): the initial thread load asks the
// server for only the LAST window of messages; older pages load on demand
// via the "Load earlier messages" button (beforeMs = oldest loaded row,
// strictly-older server filter). "Has more" is inferred when a page comes
// back full (length === window).
const THREAD_WINDOW = 75;
// Presence L1-3 (2026-09-08): heartbeat cadence + typing timers.
const PRESENCE_HEARTBEAT_MS = 25000;
// Immediate typing:true transition beats re-arm no faster than this (L2).
const TYPING_BEAT_REARM_MS = 5000;
// Composer untouched this long → typing:false (L2).
const TYPING_IDLE_MS = 10000;
// Ghost draft frames are full snapshots throttled to at most one per 250ms.
const GHOST_DRAFT_THROTTLE_MS = 250;
// Ghost socket reconnect backoff: closed before auth-ok (auth-fail class) vs
// dropped after a healthy auth.
const GHOST_BACKOFF_AUTH_FAIL_MS = 30000;
const GHOST_BACKOFF_DROP_MS = 5000;
// Chip display helpers (pure, module scope).
const presenceInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
};
const presenceFirstName = (name) =>
  (name || "").trim().split(/\s+/)[0] || "Viewer";
const MsgPageTextDisplay = ({
  phoneId,
  handleSnack: handleSnackProp,
  closeMessages,
  queryPhoneMsgTrigger,
}) => {
  // MessagesPage doesn't pass handleSnack down, so fall back to the global
  // snackbar context. Always resolves to the top-right handleSnack.
  const { handleSnack: handleSnackCtx } = useSnackbar();
  const handleSnack = handleSnackProp || handleSnackCtx;
  const [sendSMSgql] = useMutation(SEND_SMS_TEXT_STRIPPED_RETURN, {
    fetchPolicy: "network-only",
  });
  const [queryPhoneNumberCall] = useLazyQuery(GET_PHONE_TEXTS_BY_ID, {
    fetchPolicy: "network-only",
  });
  const [queryTextsSince] = useLazyQuery(GET_PHONE_TEXTS_SINCE, {
    fetchPolicy: "network-only",
  });
  const [queryPhoto] = useLazyQuery(QUERY_CLIENT_PHOTOS, {
    fetchPolicy: "network-only",
  });
  const [shadowOn, setShadowOn] = useState(false);
  //   const [fetchNewInfo, setFetchNewInfo] = useState(false);
  const [sendMesInput, setSendMesInput] = useState("");

  /* Monotonic, so two sends inside the same millisecond cannot collide. */
  const optimisticSeqRef = useRef(0);
  const [textMes, setTextMes] = useState([]);
  const [textMesImgIdsArr, setTextMesImgIdsArr] = useState([]);
  const [queryForImgs, setQueryForImgs] = useState(true);
  const [newMessageTrigger, setNewMessageTriggered] = useState(false);
  const [initLoadingDone, setInitLoadingDone] = useState(false);
  const [prevPhoneId, setPrevPhoneId] = useState("");
  // T3.1: whether older pages may exist server-side (window came back full),
  // and whether a load-older fetch is currently in flight (button disables;
  // double-click guard).
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  /*
   * P5 DELETED `pendingScrollAdjustRef` AND `suppressAutoScrollRef`.
   *
   * The first reimplemented the browser's own scroll anchoring for prepended
   * history; the second existed only to stop the scroll-to-bottom effect
   * fighting it. `column-reverse` makes the visual bottom the scroll origin,
   * so neither job exists any more. See the stylesheet.
   */
  // Newest server-clock sentReceivedTime currently held, kept in a REF (not
  // React state) so the incremental since-fetch always reads the latest value
  // without a stale closure or an extra render. Updated whenever textMes is set.
  const newestTimeRef = useRef("0");
  // Currently-selected phone id mirrored into a ref, written during render so
  // it is current BEFORE any async response handler runs. Every fetch captures
  // the id it was issued for and discards its response if this ref has moved
  // on (thread-switch race guard).
  const phoneIdRef = useRef(phoneId);
  phoneIdRef.current = phoneId;
  // Bodies with a send currently in flight — Retry taps for these are no-ops
  // until the send settles (prevents double-fire duplicate SMS).
  const sendInFlightRef = useRef(new Set());
  // Failed outbound bubbles awaiting a retry. Entries carry the phoneId they
  // belong to and SURVIVE thread switches (reviewer hardening, 2026-09-02: a
  // send that failed after a switch used to vanish with only a snackbar);
  // the render filters to the current thread via visibleFailedSends.
  const [failedSends, setFailedSends] = useState([]);
  // Server rows that already claimed a failedSends entry — each row claims at
  // most once across overlapping since-fetches (claim-once guard).
  const reconciledIdsRef = useRef(new Set());
  // Only the current thread's failed bubbles render (legacy entries without a
  // phoneId stay visible everywhere rather than silently disappearing).
  const visibleFailedSends = (failedSends || []).filter(
    (f) => !f?.phoneId || f.phoneId === phoneId
  );
  // sms_status (2026-09-04): live delivery-status from the /sms-status
  // callback — patch the matching bubble in place (a since-fetch can never
  // re-carry a row whose only change is status). LOCAL STATE ONLY.
  const handleSmsStatus = useCallback((data) => {
    if (!data?.twilioSID || !data?.status) return;
    if (data?.phoneNumId && data.phoneNumId !== phoneIdRef.current) return;
    setTextMes((prev) => {
      const rows = prev?.textMessages;
      if (!rows?.length) return prev;
      let changed = false;
      // Client-side rank guard: the server only emits rank-accepted changes,
      // but event delivery order isn't guaranteed — only progress upward.
      const RANK = { queued: 1, accepted: 1, sending: 2, sent: 3, delivered: 4, undelivered: 4, failed: 4 };
      const patched = rows.map((m) => {
        if (m?.twilioSID !== data.twilioSID) return m;
        if ((RANK[data.status] ?? 0) <= (RANK[m?.status] ?? 0)) return m;
        changed = true;
        return { ...m, status: data.status, errorCode: data.errorCode ?? m.errorCode };
      });
      return changed ? { ...prev, textMessages: patched } : prev;
    });
  }, []);
  useSSEListener("sms_status", handleSmsStatus);
  // ── Presence L1-3 (office comms, 2026-09-08) ────────────────────────────
  // L1 viewer chips + L2 typing flag ride join/heartbeat/leave GraphQL on the
  // always-on transport; L3 ghost typing opens a lazy SAME-ORIGIN WebSocket
  // only while ghostSocketEnabled && another device is co-present (ignition
  // option A — pre-warm on co-presence). Device identity is TAB_DEVICE_ID
  // (module-scope UUID minted once per tab load — F5). Presence broadcasts
  // carry NO originUserId (F14), so this tab RECEIVES its own deltas —
  // viewers are self-filtered by deviceId everywhere they're stored.
  const [presenceViewers, setPresenceViewers] = useState([]);
  const [ghostEnabled, setGhostEnabled] = useState(false);
  // Most recently updated peer draft: { deviceId, userName, text, at } | null.
  // Rendered as a TEMPORARY ghost bubble at the end of the thread (2026-09-08
  // — supersedes the old fixed-height F17 slot above the composer).
  const [ghostDraft, setGhostDraft] = useState(null);
  const [joinSmsThreadGql] = useMutation(JOIN_SMS_THREAD, {
    fetchPolicy: "no-cache",
  });
  // F11: heartbeats must never be served from / written to the cache.
  const [heartbeatSmsThreadGql] = useMutation(HEARTBEAT_SMS_THREAD, {
    fetchPolicy: "no-cache",
  });
  const [leaveSmsThreadGql] = useMutation(LEAVE_SMS_THREAD, {
    fetchPolicy: "no-cache",
  });
  // Render-time ref mirrors (same pattern as phoneIdRef above) so interval /
  // socket callbacks always read current values without stale closures.
  const sendMesInputRef = useRef("");
  sendMesInputRef.current = sendMesInput;
  const ghostEnabledRef = useRef(false);
  ghostEnabledRef.current = ghostEnabled;
  const otherViewerCountRef = useRef(0);
  otherViewerCountRef.current = presenceViewers.length;
  // Per-thread monotonic roster version (F3). Reset on join/leave.
  const rosterVRef = useRef(-1);
  const heartbeatTimerRef = useRef(null);
  // F18: skip-if-in-flight — beats never stack on a slow link. An immediate
  // (typing-transition) beat that lands mid-flight re-fires once on settle.
  const heartbeatInFlightRef = useRef(false);
  const heartbeatPendingRef = useRef(false);
  const typingRef = useRef(false);
  const typingIdleTimerRef = useRef(null);
  const lastTypingTrueBeatRef = useRef(0);
  // L3 ghost socket machinery.
  const ghostSocketRef = useRef(null);
  const ghostAuthOkRef = useRef(false);
  const ghostBackoffUntilRef = useRef(0);
  const ghostRetryTimerRef = useRef(null);
  const ghostDraftsRef = useRef(new Map()); // deviceId -> {userName,text,at}
  const draftThrottleTimerRef = useRef(null);
  const pendingDraftTextRef = useRef(null);
  const lastDraftSentAtRef = useRef(0);
  // Latest evaluateGhostSocket, callable from timers/handlers without a stale
  // closure (reassigned every render below).
  const evaluateGhostRef = useRef(null);
  // Roster from a join/heartbeat RETURN — the authoritative snapshot. It may
  // move rosterV in EITHER direction: a pm2 restart wipes presence and
  // legitimately resets the counter, and a heartbeat return must heal that
  // within one beat instead of being version-blocked forever. SSE deltas (the
  // handler further down) only ever apply FORWARD past this point.
  const applyMutationRoster = (phId, roster) => {
    if (!roster) return;
    if (phId !== phoneIdRef.current) return; // thread switched mid-flight
    const v = parseInt(roster.rosterV, 10);
    if (!Number.isNaN(v)) rosterVRef.current = v;
    setGhostEnabled(!!roster.ghostSocketEnabled);
    setPresenceViewers(
      (roster.viewers || []).filter((vw) => vw?.deviceId !== TAB_DEVICE_ID)
    );
  };
  const sendHeartbeat = async (immediate = false) => {
    const phId = phoneIdRef.current;
    if (!phId) return;
    if (document.visibilityState === "hidden") return;
    if (heartbeatInFlightRef.current) {
      // A typing transition must not be silently dropped — re-fire once when
      // the in-flight beat settles. Interval beats just skip (F18).
      if (immediate) heartbeatPendingRef.current = true;
      return;
    }
    heartbeatInFlightRef.current = true;
    try {
      const { data } = await heartbeatSmsThreadGql({
        variables: {
          phoneNumId: phId,
          deviceId: TAB_DEVICE_ID,
          typing: typingRef.current,
        },
      });
      applyMutationRoster(phId, data?.heartbeatSmsThread);
    } catch (error) {
      // Best-effort — the next interval beat retries; server sweeps stale.
    } finally {
      heartbeatInFlightRef.current = false;
      if (heartbeatPendingRef.current) {
        heartbeatPendingRef.current = false;
        sendHeartbeat(true);
      }
    }
  };
  const joinPresence = async (phId) => {
    if (!phId) return;
    rosterVRef.current = -1;
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      sendHeartbeat(false);
    }, PRESENCE_HEARTBEAT_MS);
    try {
      const { data } = await joinSmsThreadGql({
        variables: { phoneNumId: phId, deviceId: TAB_DEVICE_ID },
      });
      applyMutationRoster(phId, data?.joinSmsThread);
    } catch (error) {
      // Best-effort — the heartbeat interval keeps trying and re-registers.
    }
  };
  // Leave = stop beats, drop typing, close the ghost socket, clear roster
  // state, and tell the server (fire-and-forget). Idempotent — safe to call
  // on thread switch, tab-hidden, and unmount.
  const leavePresence = (phId) => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    heartbeatPendingRef.current = false;
    if (typingIdleTimerRef.current) {
      clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = null;
    }
    typingRef.current = false;
    rosterVRef.current = -1;
    setPresenceViewers([]);
    setGhostEnabled(false);
    closeGhostSocket();
    if (phId) {
      leaveSmsThreadGql({
        variables: { phoneNumId: phId, deviceId: TAB_DEVICE_ID },
      }).catch(() => {});
    }
  };
  // L2 typing: composer non-empty → typing:true with an immediate heartbeat
  // (re-armed no faster than every 5s); empty/sent → false immediately; 10s
  // idle → false. Only USER edits route here — the failure-path composer
  // restore in doSend deliberately does not re-raise typing.
  const handleTypingActivity = (value) => {
    const nonEmpty = (value?.length ?? 0) > 0;
    if (nonEmpty) {
      if (typingIdleTimerRef.current) clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = setTimeout(() => {
        if (typingRef.current) {
          typingRef.current = false;
          sendHeartbeat(true);
        }
      }, TYPING_IDLE_MS);
      if (!typingRef.current) {
        typingRef.current = true;
        const now = Date.now();
        if (now - lastTypingTrueBeatRef.current >= TYPING_BEAT_REARM_MS) {
          lastTypingTrueBeatRef.current = now;
          sendHeartbeat(true);
        }
        // Inside the re-arm window the flag rides the next beat instead.
      }
    } else {
      if (typingIdleTimerRef.current) {
        clearTimeout(typingIdleTimerRef.current);
        typingIdleTimerRef.current = null;
      }
      if (typingRef.current) {
        typingRef.current = false;
        sendHeartbeat(true);
      }
    }
  };
  // ── L3 ghost socket ──────────────────────────────────────────────────────
  const refreshGhostDraft = () => {
    let latest = null;
    ghostDraftsRef.current.forEach((d) => {
      if (!latest || d.at > latest.at) latest = d;
    });
    setGhostDraft(latest);
  };
  // Deliberate close: nulling the ref FIRST makes the socket's own onclose
  // identity-check bail, so no backoff/re-evaluate churn on intentional
  // teardown (thread switch, hidden, viewers→0, unmount).
  const closeGhostSocket = () => {
    if (ghostRetryTimerRef.current) {
      clearTimeout(ghostRetryTimerRef.current);
      ghostRetryTimerRef.current = null;
    }
    if (draftThrottleTimerRef.current) {
      clearTimeout(draftThrottleTimerRef.current);
      draftThrottleTimerRef.current = null;
    }
    pendingDraftTextRef.current = null;
    const ws = ghostSocketRef.current;
    if (!ws) return;
    ghostSocketRef.current = null;
    ghostAuthOkRef.current = false;
    try {
      ws.close();
    } catch (error) {
      // already closing
    }
    ghostDraftsRef.current.clear();
    setGhostDraft(null);
  };
  // Full-snapshot draft frames, throttled ≥250ms (leading + trailing edge so
  // the peer always ends on the final text). Empty text → immediate clear.
  const updateGhostDraft = (text) => {
    const ws = ghostSocketRef.current;
    const canSend =
      ws && ws.readyState === WebSocket.OPEN && ghostAuthOkRef.current;
    if (!text) {
      pendingDraftTextRef.current = null;
      if (draftThrottleTimerRef.current) {
        clearTimeout(draftThrottleTimerRef.current);
        draftThrottleTimerRef.current = null;
      }
      if (canSend) {
        try {
          ws.send(JSON.stringify({ type: "clear" }));
        } catch (error) {
          // socket died mid-send — onclose handles it
        }
      }
      return;
    }
    if (!canSend) return;
    const now = Date.now();
    const since = now - lastDraftSentAtRef.current;
    if (since >= GHOST_DRAFT_THROTTLE_MS && !draftThrottleTimerRef.current) {
      lastDraftSentAtRef.current = now;
      try {
        ws.send(JSON.stringify({ type: "draft", text }));
      } catch (error) {
        // socket died mid-send — onclose handles it
      }
      return;
    }
    pendingDraftTextRef.current = text;
    if (!draftThrottleTimerRef.current) {
      draftThrottleTimerRef.current = setTimeout(() => {
        draftThrottleTimerRef.current = null;
        const t = pendingDraftTextRef.current;
        pendingDraftTextRef.current = null;
        const ws2 = ghostSocketRef.current;
        if (
          t == null ||
          !ws2 ||
          ws2.readyState !== WebSocket.OPEN ||
          !ghostAuthOkRef.current
        )
          return;
        lastDraftSentAtRef.current = Date.now();
        try {
          ws2.send(JSON.stringify({ type: "draft", text: t }));
        } catch (error) {
          // socket died mid-send — onclose handles it
        }
      }, Math.max(50, GHOST_DRAFT_THROTTLE_MS - since));
    }
  };
  const openGhostSocket = () => {
    const forId = phoneIdRef.current;
    if (!forId) return;
    let ws;
    try {
      // SAME-ORIGIN on purpose — the nginx apex block proxies /ws/ (F8);
      // api.* is the mobile lane.
      ws = new WebSocket(
        (window.location.protocol === "https:" ? "wss://" : "ws://") +
          window.location.host +
          "/ws/thread-draft"
      );
    } catch (error) {
      ghostBackoffUntilRef.current = Date.now() + GHOST_BACKOFF_AUTH_FAIL_MS;
      return;
    }
    ghostSocketRef.current = ws;
    ghostAuthOkRef.current = false;
    ws.onopen = () => {
      if (ghostSocketRef.current !== ws) return;
      // First frame = auth (browser sockets can't send headers — F10).
      // Identity is stamped server-side at auth time; per-frame identity is
      // ignored, so it rides only here.
      const profile = Auth.getProfile();
      const userName = [profile?.data?.firstName, profile?.data?.lastName]
        .filter(Boolean)
        .join(" ");
      try {
        ws.send(
          JSON.stringify({
            type: "auth",
            token: Auth.getToken(),
            phoneNumId: forId,
            deviceId: TAB_DEVICE_ID,
            userName,
          })
        );
      } catch (error) {
        // socket died before auth — onclose applies the auth-fail backoff
      }
    };
    ws.onmessage = (evt) => {
      if (ghostSocketRef.current !== ws) return;
      let frame;
      try {
        frame = JSON.parse(evt.data);
      } catch (error) {
        return;
      }
      switch (frame?.type) {
        case "auth-ok":
          ghostAuthOkRef.current = true;
          // F1: re-send the current full draft on socket-open so a
          // burst-then-pause draft is never lost to the peer.
          if (sendMesInputRef.current) updateGhostDraft(sendMesInputRef.current);
          break;
        case "peer-joined":
          // F1: late joiner gets the current snapshot too.
          if (sendMesInputRef.current) updateGhostDraft(sendMesInputRef.current);
          break;
        case "draft":
          if (frame?.deviceId && frame.deviceId !== TAB_DEVICE_ID) {
            ghostDraftsRef.current.set(frame.deviceId, {
              deviceId: frame.deviceId,
              userName: frame.userName || "",
              text: frame.text ?? "",
              at: Date.now(),
            });
            refreshGhostDraft();
          }
          break;
        case "clear":
        case "peer-left":
          if (frame?.deviceId) {
            ghostDraftsRef.current.delete(frame.deviceId);
            refreshGhostDraft();
          }
          break;
        default:
          break;
      }
    };
    ws.onerror = () => {
      // onclose always follows — handled there.
    };
    ws.onclose = () => {
      if (ghostSocketRef.current !== ws) return; // deliberate close/superseded
      const hadAuth = ghostAuthOkRef.current;
      ghostSocketRef.current = null;
      ghostAuthOkRef.current = false;
      ghostDraftsRef.current.clear();
      setGhostDraft(null);
      // Closed before auth-ok = auth-fail class → terminal for this attempt
      // with ~30s backoff; a post-auth drop retries sooner. Reconnection only
      // ever flows through evaluateGhostSocket + its single timer — never a
      // tight loop.
      ghostBackoffUntilRef.current =
        Date.now() +
        (hadAuth ? GHOST_BACKOFF_DROP_MS : GHOST_BACKOFF_AUTH_FAIL_MS);
      evaluateGhostRef.current?.();
    };
  };
  // Ignition (option A — pre-warm on co-presence): connect while
  // ghostSocketEnabled && at least one OTHER device is viewing; disconnect
  // when that drops, the thread closes, or the tab hides. Roster-driven
  // re-evaluation with backoff (F16 gates all /ws/ attempts on the flag).
  const evaluateGhostSocket = () => {
    const want =
      ghostEnabledRef.current &&
      otherViewerCountRef.current >= 1 &&
      !!phoneIdRef.current &&
      document.visibilityState !== "hidden";
    const ws = ghostSocketRef.current;
    if (!want) {
      if (ghostRetryTimerRef.current) {
        clearTimeout(ghostRetryTimerRef.current);
        ghostRetryTimerRef.current = null;
      }
      if (ws) closeGhostSocket();
      return;
    }
    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    )
      return;
    if (ws) closeGhostSocket(); // CLOSING/CLOSED leftover
    const now = Date.now();
    if (now < ghostBackoffUntilRef.current) {
      if (!ghostRetryTimerRef.current) {
        ghostRetryTimerRef.current = setTimeout(() => {
          ghostRetryTimerRef.current = null;
          evaluateGhostRef.current?.();
        }, ghostBackoffUntilRef.current - now + 100);
      }
      return;
    }
    openGhostSocket();
  };
  evaluateGhostRef.current = evaluateGhostSocket;
  // sms_presence SSE deltas: { phoneNumId, rosterV: "<int>", viewersJson }
  // — values are STRINGS (FCM parity) and there is NO originUserId, so this
  // tab receives its own deltas (self-filtered by TAB_DEVICE_ID). Monotonic
  // rosterV guard mirrors the sms_status RANK guard above: delivery order
  // isn't guaranteed — only progress forward. NOTE: deltas carry no
  // ghostSocketEnabled; that flag only moves on join/heartbeat returns.
  const handleSmsPresence = useCallback((data) => {
    if (!data?.phoneNumId || data.phoneNumId !== phoneIdRef.current) return;
    const v = parseInt(data.rosterV, 10);
    if (Number.isNaN(v) || v <= rosterVRef.current) return;
    let viewers;
    try {
      viewers =
        typeof data.viewersJson === "string"
          ? JSON.parse(data.viewersJson)
          : data.viewersJson;
    } catch (error) {
      return;
    }
    if (!Array.isArray(viewers)) return;
    rosterVRef.current = v;
    setPresenceViewers(viewers.filter((vw) => vw?.deviceId !== TAB_DEVICE_ID));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useSSEListener("sms_presence", handleSmsPresence);
  // Presence lifecycle: join on thread select, leave on switch/unmount (the
  // cleanup closes over the OLD phoneId). Joining while hidden is skipped —
  // the visibilitychange handler rejoins on return (F13).
  useEffect(() => {
    if (!phoneId) return undefined;
    if (document.visibilityState !== "hidden") joinPresence(phoneId);
    return () => {
      leavePresence(phoneId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneId]);
  // F13: hidden → stop heartbeats + leave + drop the ghost socket (a tab
  // buried since lunch must never show as viewing); visible → rejoin.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        leavePresence(phoneIdRef.current);
      } else if (phoneIdRef.current) {
        joinPresence(phoneIdRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Re-evaluate ignition whenever the roster, the flag, or the thread moves.
  useEffect(() => {
    evaluateGhostRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghostEnabled, presenceViewers, phoneId]);
  // ── end presence L1-3 ───────────────────────────────────────────────────
  const textMesBoxRef = useRef();
  const shadowTopRef = useRef();
  const shadowBottomRef = useRef();
  const textMesRef = useRef();
  const sendMesRef = useRef();
  const shadowWrapper = useRef();
  useEffect(() => {
    if (phoneId) {
      if (prevPhoneId !== "" && prevPhoneId !== phoneId) {
        clearState();
        queryPhoneNum();
        return;
      }
      // Same phone, triggered live (SSE / backstop / reconnect): pull ONLY the
      // messages newer than what we hold and append + dedup, instead of a full
      // thread refetch.
      if (prevPhoneId === phoneId) {
        setNewMessageTriggered(true);
        queryTextsSinceIncremental();
        return;
      }
      // First load of this phone.
      queryPhoneNum();
    }
    // eslint-disable-next-line
  }, [phoneId, queryPhoneMsgTrigger]);
  useEffect(() => {
    if (phoneId) {
      const textMesBoxEl = textMesRef.current;
      if (textMesBoxEl?.offsetHeight < textMesBoxEl?.scrollHeight) {
        setShadowOn(true);
        const handleShadow = () => {
          /* Guard FIRST, so the priming call below is a clean no-op on the
             render where the divs are not mounted yet. */
          if (!shadowTopRef.current || !shadowBottomRef.current) return;
          const contentScroll =
            textMesBoxEl?.scrollHeight - textMesBoxEl?.offsetHeight;
          if (!(contentScroll > 0)) {
            /* NOTHING TO SCROLL MEANS NEITHER SHADOW. Deriving this from the
               fraction gave `fromTop = 0`, which means "at the top", and so
               painted the BOTTOM band over a thread that does not scroll. The
               pre-P5 code divided by zero, assigned `NaN`, and the invalid
               assignment happened to leave the last good value standing — so
               this was latent, not new, but the reversal is what made the
               wrong branch reachable. */
            shadowTopRef.current.style.opacity = 0;
            shadowBottomRef.current.style.opacity = 0;
            return;
          }
          /* P5: rebuilt from the BOTTOM, because that is the scroll origin
             now. `scrollTop / contentScroll` was a fraction from the top; the
             same fraction is `1 - abs(scrollTop) / contentScroll`, clamped
             because Safari can overscroll past the maximum. At the bottom that
             is 1, so the TOP shadow shows (there is content above) and the
             bottom one does not — which is the way round it always was. */
          const fromTop =
            1 - Math.min(1, Math.abs(textMesBoxEl.scrollTop) / contentScroll);
          shadowTopRef.current.style.opacity = fromTop;
          shadowBottomRef.current.style.opacity = 1 - fromTop;
        };
        textMesBoxEl.addEventListener("scroll", handleShadow);
        /*
         * PRIME IT. `handleShadow` used to have a second driver by accident:
         * the deleted `scrollTo({ top: scrollHeight })` actually moved a
         * normal column (which opens at the top), and the prepend-restore
         * assigned `scrollTop` too — both dispatched `scroll`. Under
         * `column-reverse` the container is ALREADY at the origin on open and
         * prepended history does not shift it, so no scroll event is ever
         * dispatched and the handler would never run at all. P5 deleted both
         * of its drivers.
         *
         * `shadowOn` is in this effect's deps for the same reason, and that is
         * not padding: the divs this handler writes to only render while
         * `shadowOn` is true, and `shadowOn` is set BY this effect — so on the
         * render that binds the listener the refs are still null. Without
         * `shadowOn` as a dep the effect never runs again and the priming call
         * can only ever no-op. Re-running after the refs attach is the point.
         */
        handleShadow();
        return () => {
          // Unbind the event listener on clean up
          textMesBoxEl.removeEventListener("scroll", handleShadow);
        };
      }
      if (textMesBoxEl.offsetHeight >= textMesBoxEl.scrollHeight) {
        setShadowOn(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textMes, phoneId, shadowOn]);
  /*
   * P5: THE SCROLLING IS GONE FROM HERE; only the bookkeeping is left.
   *
   * This used to `scrollTo({ top: scrollHeight })` on every open and every
   * new message, guarded by `suppressAutoScrollRef` so a load-older pass did
   * not yank a history reader. With `column-reverse` the visual bottom IS the
   * scroll origin, so a fresh open is already on the newest message and a new
   * message arriving at the DOM start appears without the viewport moving.
   *
   * The prepend-preservation `useLayoutEffect` that used to sit below this —
   * recording `scrollHeight`/`scrollTop` before an older page committed and
   * restoring `scrollTop += delta` after — is deleted outright. Older history
   * now lands at the DOM END, above the reader, where it cannot move them.
   * That effect was the browser's own scroll anchoring, written by hand.
   *
   * ONE BEHAVIOUR GENUINELY CHANGED, and it is the mirror image of the bug
   * this fixes: a reader scrolled UP in history who receives a new message
   * used to be yanked to the bottom, and now drifts by that message's height
   * instead, because the content below them grew. Strictly less disruptive
   * than the yank, but it IS different, and nobody has looked at it in a
   * browser yet.
   */
  useEffect(() => {
    if (initLoadingDone || newMessageTrigger) {
      setInitLoadingDone(false);
      setNewMessageTriggered(false);
      setPrevPhoneId(phoneId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initLoadingDone]);
  // Ghost bubble follow (L3, 2026-09-08): when the viewer is at/near the
  // bottom, keep the growing ghost bubble pinned in view — same intent as the
  // new-message scroll-to-bottom, but WITHOUT yanking a scrolled-up reader
  // (the suppressAutoScroll contract). Runs after layout, so the distance
  // check sees the bubble's NEW height; the bubble is clamped to 4 lines, so
  // a pinned reader can never drift past the threshold from growth alone,
  // while a history reader sits far beyond it and is left untouched.
  useLayoutEffect(() => {
    if (!ghostDraft?.text) return;
    const el = textMesRef.current;
    if (!el) return;
    /* Distance from the BOTTOM is `abs(scrollTop)` under `column-reverse`,
       where the origin is the bottom — it was
       `scrollHeight - scrollTop - clientHeight`. `abs` rather than `-scrollTop`
       because the sign is not something to rely on: MDN documents negative,
       older engines counted up, and Safari can overshoot the maximum while
       overscroll-bouncing. Pinning to the bottom is `scrollTop = 0`. */
    const dist = Math.abs(el.scrollTop);
    if (dist <= 150) {
      el.scrollTop = 0;
    }
  }, [ghostDraft]);
  /*
    REMOVED 2026-10-08 — an effect that called `window.scrollTo` on every
    [textMes, smaller900, textMesImgIdsArr] change, in BOTH its branches.

    It was a near-no-op only because `.text-msgs-page` was exactly
    viewport-height with `overflow: hidden`, so the document had nothing to
    scroll. That made it the landmine under any change to the page shell: the
    moment the document became scrollable it would start yanking the window on
    every message arrival and every image hydration. The thread's own scroll
    container is what should move, and it already does.

    Deleting it also closes the 600-900px dead zone: this was the only consumer
    of `useMediaQuery("(max-width:900px)")`, while the layout stacked at 600 —
    so between those widths the page ran the desktop two-pane layout with the
    mobile scroll branch. The CSS now stacks at 900 and there is no JS
    breakpoint left to disagree with it.
  */
  useEffect(() => {
    if (textMesImgIdsArr?.length > 0 && queryForImgs) {
      if (textMesImgIdsArr?.length <= 10) {
        const idOnlyArrOfArr = textMesImgIdsArr?.filter((item) => {
          if (item === null) {
            return null;
          }
          if (
            item[0]?.imageData ||
            item[0]?.s3ImageUrl ||
            item[0]?.__typename === "ClientDoorPhoto"
          ) {
            return null;
          }
          return item;
        });
        const noNull = idOnlyArrOfArr?.filter((item) => item !== null);
        if (noNull?.length > 0) {
          const idArr = noNull?.flat();
          queryPhotoById(idArr);
          return;
        } else {
          setInitLoadingDone(true);
          return;
        }
      }
      if (textMesImgIdsArr?.length > 10) {
        const sliceNum = textMesImgIdsArr?.length - 10;
        const slicedArr = textMesImgIdsArr?.slice(sliceNum);
        const idOnlyArrOfArr = slicedArr?.filter((item) => {
          if (item === null) {
            return null;
          }
          if (
            item[0]?.imageData ||
            item[0]?.s3ImageUrl ||
            item[0]?.__typename === "ClientDoorPhoto"
          ) {
            return null;
          }
          return item;
        });
        const noNull = idOnlyArrOfArr?.filter((item) => item !== null);
        if (noNull?.length > 0) {
          const idArr = noNull?.flat();
          queryPhotoById(idArr);
          return;
        } else {
          setInitLoadingDone(true);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textMesImgIdsArr]);
  // Derive the image-ref parallel array from the current sorted message list
  // so indexes always realign after merges re-sort (previously incremental
  // appends pushed refs at the tail while messages re-sorted — drift). Entries
  // already loaded into image objects are preserved by matching their _ids
  // back to each row's attachedImgs ids.
  useEffect(() => {
    const msgs = textMes?.textMessages;
    if (!Array.isArray(msgs)) return;
    setTextMesImgIdsArr((prevImgs) => {
      const byKey = new Map();
      prevImgs?.forEach((entry) => {
        if (!entry?.length) return;
        const key = entry
          .map((item) => (typeof item === "string" ? item : item?._id))
          .join("|");
        byKey.set(key, entry);
      });
      return msgs.map((textObj) => {
        if (!(textObj?.attachedImgs?.length > 0)) return null;
        const key = textObj.attachedImgs.join("|");
        return byKey.get(key) ?? textObj.attachedImgs;
      });
    });
  }, [textMes]);
  const clearState = () => {
    setTextMes([]);
    setTextMesImgIdsArr([]);
    // failedSends deliberately NOT cleared — entries are phoneId-scoped and
    // reappear when the user switches back to their thread.
    newestTimeRef.current = "0";
    setHasOlder(false);
    setLoadingOlder(false);
  };
  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    if (name === "sendMessageText") {
      /* AUTO-CAPITALISATION MOVED OUT. It is BdrInput's `autoCapSentences`
         prop now, over `utils/formatters/autoCapitalize.js`, which has
         already rewritten `event.target.value` (and put the caret back) by
         the time this runs — so all that is left here is storing it.

         The three refs this replaced only ever worked at index 0, because
         that is the one position that cannot shift under editing. The shared
         helper DIFFS the edit instead and only considers inserted text, so:
         the refusal works anywhere in a message of any length, a deletion can
         never capitalise what it exposes (the old "Https://x" → delete the H
         → "Ttps://x" bug), and urls, e-mails, decimals, abbreviations and
         ellipses are left alone. BdrInput also resets its own tracking when
         the value is replaced from outside, which is why the two
         `resetAutoCapIntent()` calls on the send and failure-restore paths
         are gone rather than moved. */
      const finalVal = value ?? "";
      setSendMesInput(finalVal);
      // Presence L2/L3: every USER composer edit drives the typing flag and
      // (when the ghost socket is live) a throttled draft snapshot.
      handleTypingActivity(finalVal);
      updateGhostDraft(finalVal);
      return;
    }
  };
  //   const scrollInputIntoView = () => {
  //     const sendRef = sendMesRef.current;
  //     const elHeight = sendRef?.offsetHeight;
  //     const scroll =
  //       window.scrollY +
  //       sendMesRef.current.getBoundingClientRect().top -
  //       window?.visualViewport.height +
  //       elHeight +
  //       80;
  //     window?.scrollTo({
  //       top: scroll,
  //       behavior: "smooth",
  //     });
  //   };
  //   const inputBlur = () => {
  //     setRefocusMes((prev) => !prev);
  //   };
  const queryPhotoById = async (idArr) => {
    // Request-identity guard: discard the response if the selected thread
    // changed while this photo fetch was in flight.
    const forId = phoneId;
    const { data } = await queryPhoto({
      variables: {
        pictureIdArr: idArr,
      },
    });
    if (forId !== phoneIdRef.current) return;
    setQueryForImgs(false);
    if (data?.getImageById) {
      setTextMesImgIdsArr((prev) => {
        const originalMapped = prev?.map((idStringArr) => {
          if (idStringArr === null) {
            return idStringArr;
          }
          const obj = idStringArr?.map((idString) => {
            const matched = data?.getImageById?.map((imageObj) => {
              if (imageObj?._id === idString) {
                return imageObj;
              }
              return null;
            });
            return matched?.filter((item) => item !== null);
          });
          return obj?.flat()?.length > 0 ? obj?.flat() : idStringArr;
        });
        return [...originalMapped];
      });
    }
    if (prevPhoneId !== phoneId || newMessageTrigger) {
      setInitLoadingDone(true);
    }
  };
  const queryPhoneNum = async () => {
    // Request-identity guard: capture the id this fetch is issued for and
    // discard the response entirely if the selection moved on mid-flight.
    const forId = phoneId;
    const localEpoch = Date.now().toString();
    const { data } = await queryPhoneNumberCall({
      variables: {
        phoneId: forId,
        localEpoch,
        // T3.1: windowed initial load — only the last THREAD_WINDOW messages.
        limit: THREAD_WINDOW,
      },
    });
    if (forId !== phoneIdRef.current) return;
    if (data?.getPhoneNumTextsById) {
      const sorted = [...data?.getPhoneNumTextsById?.textMessages]?.sort(
        (a, b) => {
          const aNum = parseInt(a?.sentReceivedTime);
          const bNum = parseInt(b?.sentReceivedTime);
          return aNum > bNum ? 1 : -1;
        }
      );
      // Image-ref parallel array is derived from textMes by effect.
      setQueryForImgs(true);
      setTextMes({ ...data?.getPhoneNumTextsById, textMessages: [...sorted] });
      newestTimeRef.current = computeNewestTime(sorted);
      // A full window means older messages may exist server-side.
      setHasOlder(sorted.length >= THREAD_WINDOW);
    }
  };
  // T3.1 load-older page: fetch the THREAD_WINDOW messages strictly older
  // than the oldest server row we hold and PREPEND them. P5: nothing has to
  // preserve the reading position any more — under `column-reverse` these
  // rows land at the DOM END, which is ABOVE the reader, so they cannot move
  // the viewport. That is the browser doing what a layout effect used to.
  // dontUpdate: true — an older page is history reading, not a fresh open;
  // it must not re-mark-read or bump the thread's activity stamp.
  const loadOlderMessages = async () => {
    if (loadingOlder || !hasOlder) return;
    const rows = (textMes?.textMessages || []).filter((m) => m?._id);
    const oldest = rows[0]?.sentReceivedTime;
    if (!oldest) return;
    const forId = phoneId;
    setLoadingOlder(true);
    try {
      const { data } = await queryPhoneNumberCall({
        variables: {
          phoneId: forId,
          localEpoch: Date.now().toString(),
          dontUpdate: true,
          limit: THREAD_WINDOW,
          beforeMs: oldest,
        },
      });
      if (forId !== phoneIdRef.current) return;
      const older = data?.getPhoneNumTextsById?.textMessages || [];
      // Short page = nothing further back (load-older contract).
      if (older.length < THREAD_WINDOW) setHasOlder(false);
      if (older.length === 0) return;
      setTextMes((prev) => {
        const existing = Array.isArray(prev?.textMessages)
          ? [...prev.textMessages]
          : [];
        const seenIds = new Set(
          existing.filter((m) => m?._id).map((m) => m._id)
        );
        const toPrepend = older
          .filter((m) => m?._id && !seenIds.has(m._id))
          .sort((a, b) => {
            const aNum = parseInt(a?.sentReceivedTime);
            const bNum = parseInt(b?.sentReceivedTime);
            return aNum > bNum ? 1 : -1;
          });
        return { ...prev, textMessages: [...toPrepend, ...existing] };
      });
      // newestTimeRef deliberately untouched — older rows never advance the
      // since-fetch cursor.
    } finally {
      setLoadingOlder(false);
    }
  };
  // Largest numeric sentReceivedTime in a message array (server-clock epoch
  // ms as a string). Used to seed the incremental since-fetch cursor.
  // Rows without a server _id (optimistic bubbles) carry CLIENT-clock times
  // and are excluded — they must never advance the cursor (clock skew would
  // hide real server rows).
  const computeNewestTime = (msgs) => {
    let max = 0;
    msgs?.forEach((m) => {
      if (!m?._id) return;
      const t = parseInt(m?.sentReceivedTime);
      if (!Number.isNaN(t) && t > max) max = t;
    });
    return max.toString();
  };
  // Incremental live-append: fetch only messages at/after the newest we hold,
  // dedup by subdoc _id, and drop any optimistic (no-_id) bubble whose
  // body+outgoingBool now has a real server row.
  const queryTextsSinceIncremental = async () => {
    // Request-identity guard: capture the id this fetch is issued for and
    // discard the response entirely if the selection moved on mid-flight
    // (prevents merging thread A's messages into thread B).
    const forId = phoneId;
    const since = newestTimeRef.current || "0";
    const localEpoch = Date.now().toString();
    const { data } = await queryTextsSince({
      variables: { phoneId: forId, sinceEpoch: since, localEpoch },
    });
    if (forId !== phoneIdRef.current) return;
    const incoming = data?.getPhoneNumTextsSince?.textMessages;
    if (!incoming || incoming.length === 0) {
      setInitLoadingDone(true);
      return;
    }
    // Rows we already held before this batch — used below to reconcile only
    // NEWLY-arrived server rows against local failed bubbles.
    const knownIds = new Set(
      (textMes?.textMessages || []).filter((m) => m?._id).map((m) => m._id)
    );
    setTextMes((prev) => {
      const existing = Array.isArray(prev?.textMessages)
        ? [...prev.textMessages]
        : [];
      const seenIds = new Set(
        existing.filter((m) => m?._id).map((m) => m._id)
      );
      // Drop optimistic bubbles (no _id) that the server has now confirmed.
      const incomingKeys = new Set(
        incoming.map((m) => `${m?.outgoingBool}|${m?.body}`)
      );
      const pruned = existing.filter((m) => {
        if (m?._id) return true; // real row, keep
        // optimistic bubble — drop if a server row matches body+direction
        return !incomingKeys.has(`${m?.outgoingBool}|${m?.body}`);
      });
      const toAppend = incoming.filter((m) => !m?._id || !seenIds.has(m._id));
      const merged = [...pruned, ...toAppend].sort((a, b) => {
        const aNum = parseInt(a?.sentReceivedTime);
        const bNum = parseInt(b?.sentReceivedTime);
        return aNum > bNum ? 1 : -1;
      });
      return {
        ...prev,
        recentTime:
          data?.getPhoneNumTextsSince?.recentTime ?? prev?.recentTime,
        textMessages: merged,
      };
    });
    // Cursor advance moved OUT of the updater (updaters must be pure). The
    // newest _id row in the merged array is max(held cursor, newest incoming).
    const incomingNewest = computeNewestTime(incoming);
    if (parseInt(incomingNewest) > parseInt(newestTimeRef.current || "0")) {
      newestTimeRef.current = incomingNewest;
    }
    // Image-ref array itself is derived from textMes by effect; here we only
    // flag whether a fresh photo query pass is warranted.
    const hasNewImgRows = incoming.some((m) => m?.attachedImgs?.length > 0);
    if (hasNewImgRows) {
      setQueryForImgs(true);
    } else {
      setInitLoadingDone(true);
    }
    // A server row for a failed outbound now supersedes its local bubble.
    reconcileFailedSends(incoming.filter((m) => m?._id && !knownIds.has(m._id)));
  };
  // Failed-bubble reconciliation: newly-arrived server rows that are outgoing
  // with status "failed" supersede matching local failedSends bubbles (the
  // server row is now the single retryable bubble). Conservative one-to-one
  // match: same body, each server row claims at most one local entry, and it
  // claims the entry whose client ts is closest to the row's server time — so
  // distinct identical-text failures aren't over-collapsed.
  const reconcileFailedSends = (rows) => {
    // Widened (collision reviewer F2, 2026-09-02): a send whose GraphQL
    // RESPONSE was lost in transit still delivered a real SMS — the server
    // row arrives as queued/sent, not failed, and the stale local bubble
    // would invite a duplicate Retry. So ANY outgoing server row can claim a
    // matching local entry: failed rows at any gap (closest-first), non-failed
    // rows only within a ±2 min window of the entry's timestamp.
    const NON_FAILED_CLAIM_WINDOW_MS = 120000;
    const serverOutgoing = rows?.filter(
      (m) =>
        m?._id &&
        m?.outgoingBool === true &&
        !reconciledIdsRef.current.has(m._id)
    );
    if (!serverOutgoing?.length) return;
    // Pairing runs OUTSIDE the setState updater (updaters must stay pure —
    // T2.10). The closure list can be a beat stale: a reference-miss on a
    // concurrently-changed entry is a safe no-op, and the claim-once set
    // prevents any double-claim on the next pass.
    const threadId = phoneIdRef.current;
    const current = failedSends || [];
    const claimedEntries = new Set();
    serverOutgoing.forEach((row) => {
      let best = null;
      let bestGap = Infinity;
      current.forEach((f) => {
        if (claimedEntries.has(f) || f?.body !== row?.body) return;
        if (f?.phoneId && f.phoneId !== threadId) return;
        const gap = Math.abs(parseInt(row?.sentReceivedTime) - parseInt(f?.ts));
        const gapNum = Number.isNaN(gap) ? Number.MAX_SAFE_INTEGER : gap;
        if (row?.status !== "failed" && gapNum > NON_FAILED_CLAIM_WINDOW_MS)
          return;
        if (gapNum < bestGap) {
          bestGap = gapNum;
          best = f;
        }
      });
      if (best) {
        claimedEntries.add(best);
        reconciledIdsRef.current.add(row._id);
      }
    });
    if (claimedEntries.size === 0) return;
    setFailedSends((prev) => prev.filter((f) => !claimedEntries.has(f)));
  };
  // Shared send routine. `retryBody` lets a failed bubble's Retry re-send
  // without retyping. On failure the typed text is preserved (or restored).
  const doSend = async (retryBody) => {
    const body = retryBody ?? sendMesInput;
    if (!body || body.length === 0) return;
    // Send-safety guard: if state still holds a DIFFERENT thread than the
    // selected phoneId, the switch hasn't settled — abort rather than risk
    // sending to the wrong customer.
    if (textMes?._id && textMes._id !== phoneId) {
      handleSnack("Conversation changed — send cancelled", 4000);
      return;
    }
    // Identity this send is issued for — response handling is discarded if
    // the user switches threads while it is in flight.
    const forId = phoneId;
    // Retry double-fire guard: a Retry tap for a body already in flight ON
    // THIS THREAD is a no-op. (Initial sends stay unguarded — the Send button
    // self-guards, and deliberately sending the same text twice is
    // legitimate.) Thread-scoped key so an identical body in flight to a
    // different thread doesn't block this one.
    const flightKey = `${forId}|${body}`;
    if (retryBody != null && sendInFlightRef.current.has(flightKey)) return;
    const date = Date.now().toString();
    const obj = {
      textBody: body,
      localTime: date,
      phoneNumId: phoneId || null,
      sendToNum: textMes?.phoneNumber || null,
    };
    // Optimistic queued bubble (no _id) so the UI shows the message
    // immediately. Includes status + null error fields so the bubble-render
    // branching is consistent before the server row arrives.
    const optimistic = {
      /* A stable identity BEFORE the server gives it an `_id`. Keyed off the
         array index, an optimistic row's key changed whenever
         `loadOlderMessages` prepended — remounting the one row whose state
         (a pending send) is the most visible on screen. */
      clientId: `opt-${date}-${optimisticSeqRef.current++}`,
      body: obj.textBody,
      outgoingBool: true,
      sentReceivedTime: date,
      twilioSID: null,
      status: "queued",
      errorCode: null,
      errorMessage: null,
      statusUpdatedAt: null,
    };
    setTextMes((prev) => ({
      ...prev,
      textMessages: [...(prev?.textMessages || []), optimistic],
    }));
    setInitLoadingDone(true);
    // Clear the composer optimistically; restored from `body` on failure.
    // Presence L2/L3: sent → typing:false immediately + clear the peers'
    // ghost view. (The failure-path restore below deliberately does NOT
    // re-raise typing — only real keystrokes do.)
    if (!retryBody) {
      setSendMesInput("");
      handleTypingActivity("");
      updateGhostDraft("");
    }
    sendInFlightRef.current.add(flightKey);
    try {
      const { data } = await sendSMSgql({ variables: { ...obj } });
      // Thread switched while the send was in flight: the send itself went to
      // the correct (captured) number, but this component now shows another
      // thread — do not merge the response into it.
      if (forId !== phoneIdRef.current) return;
      const serverMsgs = data?.sendSMS?.textMessages;
      if (serverMsgs?.length > 0) {
        // Rows held before this response (for failed-bubble reconciliation).
        const knownIds = new Set(
          (textMes?.textMessages || []).filter((m) => m?._id).map((m) => m._id)
        );
        // Swap the optimistic bubble for the authoritative server row(s),
        // deduped by _id; drop the optimistic by body+direction match.
        setTextMes((prev) => {
          const incomingKeys = new Set(
            serverMsgs.map((m) => `${m?.outgoingBool}|${m?.body}`)
          );
          const seenIds = new Set();
          const pruned = (prev?.textMessages || []).filter((m) => {
            if (m?._id) {
              seenIds.add(m._id);
              return true;
            }
            return !incomingKeys.has(`${m?.outgoingBool}|${m?.body}`);
          });
          const toAppend = serverMsgs.filter(
            (m) => !m?._id || !seenIds.has(m._id)
          );
          const merged = [...pruned, ...toAppend].sort((a, b) => {
            return parseInt(a?.sentReceivedTime) > parseInt(b?.sentReceivedTime)
              ? 1
              : -1;
          });
          return {
            ...prev,
            recentTime: data?.sendSMS?.recentTime ?? prev?.recentTime,
            textMessages: merged,
          };
        });
        // Cursor advance kept OUT of the updater (updaters must be pure).
        // serverMsgs is the whole thread, so its newest _id row bounds merged.
        const serverNewest = computeNewestTime(serverMsgs);
        if (parseInt(serverNewest) > parseInt(newestTimeRef.current || "0")) {
          newestTimeRef.current = serverNewest;
        }
        // A successful retry clears any matching failed bubble on this thread.
        if (retryBody) {
          setFailedSends((prev) =>
            prev.filter(
              (f) =>
                !(
                  f.body === retryBody &&
                  (!f.phoneId || f.phoneId === forId)
                )
            )
          );
        }
        // Older failed rows the send response surfaced supersede local bubbles.
        reconcileFailedSends(
          serverMsgs.filter((m) => m?._id && !knownIds.has(m._id))
        );
      }
    } catch (error) {
      // sendSMS now throws with extensions.code on failure. Remove the
      // optimistic bubble, preserve the typed text, surface the reason, and
      // record a retryable failed bubble.
      const message = smsErrorMessage(error);
      // Thread switched mid-flight: surface the failure AND record a
      // retryable bubble against the ORIGINAL thread (entries are
      // phoneId-scoped and survive switches), but touch no thread state.
      if (forId !== phoneIdRef.current) {
        handleSnack(message, 4000);
        setFailedSends((prev) => [
          ...prev.filter((f) => !(f.body === body && f.phoneId === forId)),
          {
            body,
            reason: message,
            code: error?.graphQLErrors?.[0]?.extensions?.code || null,
            ts: date,
            phoneId: forId,
          },
        ]);
        return;
      }
      setTextMes((prev) => ({
        ...prev,
        textMessages: (prev?.textMessages || []).filter(
          (m) => m !== optimistic
        ),
      }));
      if (!retryBody) {
        setSendMesInput(body);
      }
      handleSnack(message, 4000);
      setFailedSends((prev) => [
        ...prev.filter(
          (f) => !(f.body === body && (!f.phoneId || f.phoneId === forId))
        ),
        {
          body,
          reason: message,
          code: error?.graphQLErrors?.[0]?.extensions?.code || null,
          ts: date,
          phoneId: forId,
        },
      ]);
    } finally {
      sendInFlightRef.current.delete(flightKey);
    }
  };
  const retryFailedSend = (body) => {
    doSend(body);
  };

  /*
   * CONSENT SOFT-CONFIRM — was a `window.confirm`, is now the Send button's
   * own `confirm` gate (see the button for the wiring).
   *
   * `window.confirm` is the one dialog in the app that cannot be styled, is
   * not announced like the rest, and blocks the whole thread while it is up.
   * It also meant `sendMessageCall` existed purely to ask a question, so the
   * gate lived a function away from the control that triggers it.
   *
   * STILL CONDITIONAL. `confirm` takes a computed boolean, so a number with
   * confirmed consent sends on ONE click exactly as it did before — this is
   * not a new prompt on the common path.
   */
  const needsConsentConfirm = textMes?.textConsent !== true;
  const consentNote =
    textMes?.textConsent === false
      ? "This recipient texted STOP. Twilio will reject the message and it will show as failed."
      : "This number has not confirmed text consent.";

  /*
   * P5: EVERYTHING IN THE SCROLLER IS BUILT NEWEST-FIRST.
   *
   * `column-reverse` paints the first DOM child at the visual bottom, so the
   * DOM order is the reverse of the reading order. Both lists are reversed on
   * COPIES — they come from state, and `Array.prototype.reverse` mutates.
   *
   * `orderedMessages` carries the ORIGINAL index with each row, and that is
   * not a nicety: `textMesImgIdsArr` is built index-parallel to
   * `textMes.textMessages` in ascending order, and the render also compares
   * `textMessages.length - 10` against the index to decide which images to
   * hydrate. Reversing with the map's own index would have silently pointed
   * every bubble at another bubble's images.
   */
  const orderedMessages = (textMes?.textMessages || [])
    .map((mesObj, index) => ({ mesObj, index }))
    .reverse();

  /*
   * The failed-send bubbles, built ONCE.
   *
   * This markup was written out twice — once after the message list and once
   * in the "no messages but there are failures" branch — as two verbatim
   * copies that had to be kept in step by hand. An ARRAY of elements, not a
   * component declared in this body: a component defined here would be a new
   * function identity on every render, so React would unmount and remount the
   * subtree instead of updating it (the bug that cost the task-note field its
   * focus on every keystroke).
   */
  const failedSendRows = (visibleFailedSends || []).map((fail, failIndex) => (
    <div
      key={`failed-send-${fail?.ts || fail?.body}-${failIndex}`}
      className="msg-page-texts-mes-box-out mes-box-scale"
    >
      <div className="msg-page-texts-mes-item msg-page-texts-mes-item-out">
        <div>{fail?.body}</div>
        <div className="msg-page-texts-mes-failed">
          <span>{fail?.reason || "Failed to send"}</span>
          <BdrButton
            size="sm"
            variant="action"
            onClick={() => retryFailedSend(fail?.body)}
          >
            Retry
          </BdrButton>
        </div>
      </div>
    </div>
  ));

  /* Reversed on a COPY. `failedSendRows` is in fact a fresh array from
     `.map()` on this render, so the spread is not strictly required today —
     but `reverse()` mutates in place, this reads as a state-derived list, and
     one edit that makes it memoised would turn an in-place reverse into a
     list that flips on every render. `orderedMessages` above is safe for the
     same reason and spreads for the same one. */
  const reversedFailedSendRows = [...failedSendRows].reverse();

  return (
    <div ref={textMesBoxRef} className="msg-page-texts-box bdr-grad-container-heavy bdr-border-std">
      {phoneId?.length > 0 ? (
        <div className="msg-page-texts-text-and-num-box">
          <div ref={shadowWrapper} className="msg-page-texts-shadow-wrapper">
            {presenceViewers?.length > 0 && (
              <div className="msg-page-presence-chips">
                {presenceViewers.map((vw) => (
                  <div
                    key={`presence-chip-${vw?.deviceId}`}
                    className="msg-page-presence-chip bdr-border-thin bdr-shadow-subtle"
                    title={vw?.userName || "Viewer"}
                  >
                    <span className="msg-page-presence-chip-initials">
                      {presenceInitials(vw?.userName)}
                    </span>
                    <span className="msg-page-presence-chip-name">
                      {presenceFirstName(vw?.userName)}
                    </span>
                    {vw?.typing && (
                      <span className="msg-page-presence-chip-typing">
                        typing…
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {shadowOn && (
              <>
                <div
                  ref={shadowTopRef}
                  className="msg-page-texts-shadow msg-page-texts-shadow--top"
                ></div>
                <div
                  ref={shadowBottomRef}
                  className="msg-page-texts-shadow msg-page-texts-shadow--bottom"
                ></div>
              </>
            )}
            {/*
              THE CHILDREN OF THIS SCROLLER ARE IN BOTTOM-UP ORDER (P5).

              It is `flex-direction: column-reverse`, so the FIRST child paints
              at the visual bottom. Reading order on screen, top to bottom, is
              therefore the reverse of what is written here:

                  "Load earlier messages"   (last child  — visual top)
                  oldest … newest message  (reversed copy)
                  failed sends              (reversed copy)
                  a colleague's ghost draft (FIRST child — visual bottom)

              Get this order wrong and the page still looks plausible while the
              load-older button sits underneath the newest message.
            */}
            <div ref={textMesRef} className="msg-page-texts-mes-box">
              {/* L3 ghost bubble (2026-09-08, supersedes the fixed slot):
                  a colleague's live draft rendered as a TEMPORARY last
                  bubble — always the newest position, outgoing (right)
                  side. It takes `--bdr-grad-panel`; it used to hard-code the
                  blue→purple that token USED to hold, which the V2 pass
                  retired (see the rule in the stylesheet). Cleared by the
                  existing ghost state transitions (clear/peer-left/close). */}
              {ghostDraft?.text?.length > 0 && (
                <div className="msg-page-texts-mes-box-out">
                  <div className="msg-page-texts-mes-item msg-page-ghost-bubble">
                    <div className="msg-page-ghost-bubble-caption">
                      {`${presenceFirstName(ghostDraft?.userName)} · typing…`}
                    </div>
                    <div className="msg-page-ghost-bubble-text">
                      {ghostDraft.text}
                    </div>
                  </div>
                </div>
              )}
              {textMes?.textMessages && textMes?.textMessages?.length > 0 ? (
                <>
                  {reversedFailedSendRows}
                  {orderedMessages.map(({ mesObj, index }) => {
                    const msg = mesObj?.body;
                    const timeStr = BAGroupTimeFormat(
                      mesObj?.sentReceivedTime,
                      true,
                      true
                    );
                    const today = /today/gi.test(timeStr);
                    const oneYear = 1000 * 60 * 60 * 24 * 365;
                    const olderThenAYear =
                      Date.now() - oneYear > parseInt(mesObj?.sentReceivedTime);
                    const time = today
                      ? `${timeStr?.split("@")[1]}`
                      : olderThenAYear
                      ? `${timeStr?.split(",")[1]?.replace(/@/gi, "-")}`
                      : `${BAGroupTimeFormat(
                          mesObj?.sentReceivedTime,
                          true,
                          false
                        )?.replace(/@/gi, "-")}`;
                    return (
                      <div
                        /*
                          B2: keyed by _id, NOT by index.
                          `loadOlderMessages` PREPENDS, which shifts every
                          index — so React kept the existing DOM nodes for
                          what was now different content and mounted the rest
                          fresh. Every visible bubble (and its <img>) was torn
                          down and rebuilt on each "Load earlier messages",
                          replaying the scale-in animation; and because that
                          remount lands in the same commit the scroll-
                          preservation effect measures, its delta was taken
                          against a temporarily shorter thread.
                          Optimistic rows genuinely have no _id yet.
                        */
                        key={mesObj?._id || mesObj?.clientId || `idx-${index}`}
                        className={`msg-page-texts-mes-box-${
                          mesObj?.outgoingBool ? "out" : "inc"
                        } mes-box-scale`}
                        // ${mesObj?.attachedImgs?.length > 0 ? "mes-box-scale" : ""}
                      >
                        <div
                          className={`msg-page-texts-mes-item ${
                            mesObj?.outgoingBool
                              ? "msg-page-texts-mes-item-out"
                              : ""
                          }`}
                        >
                          {mesObj?.attachedImgs?.length > 0 &&
                          textMesImgIdsArr?.[index]?.[0]?.length === 24 ? (
                            textMes?.textMessages?.length - 10 < index ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : (
                              <></>
                            )
                          ) : mesObj?.attachedImgs?.length === 1 ? (
                            textMesImgIdsArr[index]?.map(
                              (imgObj, indexOfImg) => {
                                const imgId = "imgEl-" + imgObj._id;
                                return (
                                  <div
                                    key={`text-img-key-${indexOfImg}`}
                                    className="msg-page-texts-mes-item-single-img-box"
                                    // style={{ height: `${maxImgHeight}px` }}
                                  >
                                    <img
                                      className="msg-page-text-img"
                                      src={imgObj?.s3ImageUrl ?? baImageUrl(imgObj?._id)}
                                      alt="text"
                                      id={imgId}
                                    />
                                    <BdrButton
                                      size="sm"
                                      variant="action"
                                      iconOnly
                                      icon={
                                        <ContentCopyIcon fontSize={"inherit"} />
                                      }
                                      ariaLabel="Copy Image"
                                      className="msg-page-texts-btn-copy"
                                      onPointerUp={async () => {
                                        const img = document.querySelector(
                                          `#${imgId}`
                                        );
                                        const blob = new ClipboardItem({
                                          "image/png": copyImageToClip(img.src),
                                        });
                                        navigator.clipboard
                                          .write([blob])
                                          .then(() =>
                                            handleSnack("Image copied", 1500)
                                          )
                                          .catch((err) => {
                                            console.error(
                                              "[MsgPageTextDisplay] copy image failed",
                                              { src: img.src, err }
                                            );
                                            handleSnack(
                                              "Couldn't copy the image",
                                              2500
                                            );
                                          });
                                      }}
                                    />
                                  </div>
                                );
                              }
                            )
                          ) : mesObj?.attachedImgs?.length > 1 ? (
                            <MsgImgShuffle
                              imgArr={textMesImgIdsArr[index]}
                              handleSnack={handleSnack}
                            />
                          ) : (
                            <></>
                          )}
                          <div
                            className={`msg-page-texts-mes-item ${
                              mesObj?.outgoingBool
                                ? ""
                                : "msg-page-texts-mes-item-inc"
                            }`}
                          >
                            {textMesImgIdsArr?.[index]?.[0]?.length === 24 &&
                              textMes?.textMessages?.length - 10 > index && (
                                <BdrButton
                                  size="sm"
                                  variant="action"
                                  onClick={() => {
                                    queryPhotoById(textMesImgIdsArr[index]);
                                  }}
                                >
                                  Load Images
                                </BdrButton>
                              )}
                            <div>{msg}</div>
                            <div className="msg-page-texts-mes-item-date">{`- ${time}`}</div>
                            {mesObj?.outgoingBool &&
                              mesObj?.status === "queued" && (
                                <div className="msg-page-texts-mes-status">
                                  Sending…
                                </div>
                              )}
                            {mesObj?.outgoingBool &&
                              (mesObj?.status === "failed" ||
                                mesObj?.status === "undelivered") && (
                                <div className="msg-page-texts-mes-failed">
                                  <span>
                                    {mesObj?.errorMessage ||
                                      "Failed to send"}
                                  </span>
                                  <BdrButton
                                    size="sm"
                                    variant="action"
                                    onClick={() =>
                                      retryFailedSend(mesObj?.body)
                                    }
                                  >
                                    Retry
                                  </BdrButton>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {hasOlder && (
                    <div className="msg-page-texts-load-older-box">
                      <BdrButton
                        size="sm"
                        variant="action"
                        disabled={loadingOlder}
                        onClick={loadOlderMessages}
                      >
                        {loadingOlder ? "Loading…" : "Load earlier messages"}
                      </BdrButton>
                    </div>
                  )}
                </>
              ) : visibleFailedSends?.length > 0 ? (
                <>{reversedFailedSendRows}</>
              ) : (
                <div></div>
              )}
            </div>
          </div>
          <div className="msg-page-texts-messenger-box">
            <BdrInput
              className="msg-send-field"
              inputRef={sendMesRef}
              type="text"
              label={"Message"}
              name="sendMessageText"
              onChange={handleChange}
              autoCapSentences
              // onFocus={scrollInputIntoView}
              // onBlur={inputBlur}
              value={sendMesInput}
              multiline
              rows={1}
            />
            <BdrButton
              variant="confirm"
              /* `!sendMesInput?.length`, not `!sendMesInput?.length > 0`.
                 The old form worked by accident: it negated the length to a
                 boolean and THEN compared it to 0, so `true > 0` happened to
                 be truthy. */
              disabled={!sendMesInput?.length}
              /* The consent gate — see `needsConsentConfirm` above. A
                 conditional boolean, so the common path is still one click. */
              confirm={needsConsentConfirm}
              confirmTitle="Send without confirmed consent?"
              confirmLabel="Send anyway"
              confirmVariant={
                textMes?.textConsent === false ? "danger" : "confirm"
              }
              note={consentNote}
              /* NOT `onClick={doSend}`. The unconfirmed path calls
                 `onClick(event)` while the confirmed path calls `onClick()`,
                 and `doSend`'s first parameter is `retryBody` — so passing it
                 directly would hand it a truthy event on the common path, the
                 composer would not clear, and the event object would be sent
                 as the message body. */
              onClick={() => doSend()}
            >
              Send
            </BdrButton>
            {/* The `bdr-btn` recipe as classes, since OfficeShortcuts applies
                this to its own <button>. It used to be handed
                `msg-page-texts-btn`, a local hand-rolled recipe that P3
                deleted.

                NO `bdr-btn--sm` HERE. P3 added it and that was wrong: Send
                beside it is a default `md` BdrButton, so the two sat at
                `--bdr-control-h` 36px and `--bdr-control-h-sm` 30px — a
                visible 6px mismatch in a row that aligns on `flex-end`. They
                matched before P3, when both used the one deleted recipe.
                Dropping the size modifier is the fix; a height override here
                would fight `.bdr-btn`'s own min-height instead. */}
            <OfficeShortcuts
              handleSnack={handleSnack}
              passedBtnCss={"bdr-btn bdr-btn--action"}
            />
          </div>
        </div>
      ) : (
        <p className="bdr-empty">No number selected</p>
      )}
    </div>
  );
};
export default MsgPageTextDisplay;
